import classNames from 'classnames';
import React, { ReactElement, ReactNode } from 'react';
import styles from './masonry-grid.module.scss';

export type MasonryGridFillDirection = 'row' | 'column';

type MasonryGridStyle = React.CSSProperties & {
  '--masonry-gap'?: string;
};

export interface MasonryGridProps {
  /** Whether to use browser-balanced masonry columns */
  balanced?: boolean;
  /** Components to render in the masonry grid */
  children: ReactNode;
  /** Optional class name for the root element */
  className?: string;
  /** Optional class name for each generated column wrapper */
  columnClassName?: string;
  /** Number of columns in the masonry layout */
  columns?: number;
  /** Fill items by row first or column first */
  fillDirection?: MasonryGridFillDirection;
  /** Gap between columns and items */
  gap?: number | string;
  /** Optional class name for each generated item wrapper */
  itemClassName?: string;
}

type MasonryItem = {
  key: string;
  node: ReactNode;
};

const normalizeColumns = (columns: number) =>
  Number.isFinite(columns) ? Math.max(1, Math.floor(columns)) : 1;

const createColumnBuckets = (
  items: MasonryItem[],
  columns: number,
  fillDirection: MasonryGridFillDirection,
) => {
  const buckets: MasonryItem[][] = Array.from({ length: columns }, () => []);

  if (!items.length) {
    return buckets;
  }

  if (fillDirection === 'row') {
    items.forEach((item, index) => {
      buckets[index % columns].push(item);
    });

    return buckets;
  }

  const rows = Math.ceil(items.length / columns);

  items.forEach((item, index) => {
    const columnIndex = Math.min(columns - 1, Math.floor(index / rows));

    buckets[columnIndex].push(item);
  });

  return buckets;
};

const createBalancedItems = (
  items: MasonryItem[],
  columns: number,
  fillDirection: MasonryGridFillDirection,
) => {
  if (fillDirection === 'column' || !items.length || columns === 1) {
    return items;
  }

  const rows = Math.ceil(items.length / columns);
  const orderedItems: MasonryItem[] = [];

  for (let columnIndex = 0; columnIndex < columns; columnIndex += 1) {
    for (let rowIndex = 0; rowIndex < rows; rowIndex += 1) {
      const sourceIndex = rowIndex * columns + columnIndex;

      if (sourceIndex < items.length) {
        orderedItems.push(items[sourceIndex]);
      }
    }
  }

  return orderedItems;
};

const MasonryGrid = ({
  balanced = false,
  className,
  children,
  columns = 3,
  fillDirection = 'row',
  gap,
  columnClassName,
  itemClassName,
}: MasonryGridProps) => {
  const resolvedColumns = normalizeColumns(columns);
  const normalizedGap = typeof gap === 'number' ? `${gap}px` : gap;

  const masonryItems = React.Children.toArray(children).map((child, index) => {
    const childKey = (child as ReactElement)?.key;

    return {
      key: childKey !== null ? String(childKey) : `masonry-item-${index}`,
      node: child,
    };
  });

  const balancedItems = createBalancedItems(
    masonryItems,
    resolvedColumns,
    fillDirection,
  );

  const columnBuckets = createColumnBuckets(
    masonryItems,
    resolvedColumns,
    fillDirection,
  );
  const style: MasonryGridStyle = {
    '--masonry-gap': normalizedGap,
    ...(balanced
      ? { columnCount: resolvedColumns }
      : { gridTemplateColumns: `repeat(${resolvedColumns}, minmax(0, 1fr))` }),
  };

  if (balanced) {
    return (
      <div
        className={classNames(styles.component, styles.isBalanced, className)}
        style={style}
      >
        {balancedItems.map(item => (
          <div
            key={item.key}
            className={classNames(styles.item, itemClassName)}
          >
            {item.node}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className={classNames(styles.component, className)} style={style}>
      {columnBuckets.map((columnItems, columnIndex) => (
        <div
          key={`masonry-column-${columnIndex}`}
          className={classNames(styles.column, columnClassName)}
        >
          {columnItems.map(item => (
            <div
              key={item.key}
              className={classNames(styles.item, itemClassName)}
            >
              {item.node}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

MasonryGrid.displayName = 'MasonryGrid';

export default MasonryGrid;
